/** Dynamic normalization for cargo heating onboarding detail sections. */

import { extractUploadedFilesFromRow } from './cargo-heating-onboarding-view.util';
import {
  buildLiquidCargoDynamicTable,
  LIQUID_CARGO_EMPTY_MESSAGE,
  resolveLiquidCargoRows,
} from './cargo-heating-liquid-cargo.util';
import {
  OnboardingDetailRow,
  OnboardingDetailSection,
  OnboardingDetailTable,
} from './onboarding-vessel-mapper.util';

const EXCLUDED_KEYS = new Set([
  'id',
  'token',
  'session',
  'password',
  'authorization',
  'raw',
  'binary',
  'base64',
]);

const ARRAY_KEYS = new Set([
  'tanks',
  'heating_tanks',
  'liquid_cargo',
  'liquidCargo',
  'pipeline_sections',
  'pipelineSections',
  'uploaded_files',
  'uploadedFiles',
  'onboarding_modules',
]);

interface FieldConfig {
  label: string;
  aliases: string[];
  type?: 'text' | 'number';
  readOnly?: boolean;
}

interface SectionConfig {
  id: string;
  title: string;
  spanFull?: boolean;
  fields: FieldConfig[];
}

const KNOWN_SECTIONS: SectionConfig[] = [
  {
    id: 'heating-configuration',
    title: 'Heating Configuration',
    fields: [
      { label: 'Heating Arrangement', aliases: ['heating_arrangement', 'heatingArrangement'] },
      { label: 'Number of Tanks', aliases: ['number_of_tanks', 'numberOfTanks'], type: 'number' },
      { label: 'Steam Pipes Insulation', aliases: ['steam_pipes_insulation', 'steamPipesInsulation'] },
      { label: 'EGCS Scrubber', aliases: ['egcs_scrubber', 'egcsScrubber'] },
      { label: 'Quick Onboarding', aliases: ['quick_onboarding', 'quickOnboarding'] },
    ],
  },
  {
    id: 'boiler-performance',
    title: 'Boiler Performance',
    fields: [
      { label: 'Boiler Make', aliases: ['boiler_make', 'boilerMake', 'boiler_manufacturer'] },
      { label: 'Number of Boilers', aliases: ['number_of_boilers', 'numberOfBoilers'], type: 'number' },
      { label: 'Boiler Capacity (kg/hr)', aliases: ['boiler_capacity', 'boilerCapacity'], type: 'number' },
      {
        label: 'Aux Boiler Fuel Consumption (kg/hr)',
        aliases: ['aux_boiler_fuel_consumption_kg_hr', 'auxBoilerFuelConsumptionKgHr'],
        type: 'number',
      },
      {
        label: 'Max Efficiency (%)',
        aliases: ['max_efficiency_percent', 'maxEfficiencyPercent', 'max_efficiency', 'maxEfficiency'],
        type: 'number',
      },
      {
        label: 'Steam Production (kg/hr)',
        aliases: [
          'steam_production_kg_hr',
          'steamProductionKgHr',
          'steam_production_at_max_efficiency',
        ],
        type: 'number',
      },
      { label: 'Boiler Load (%)', aliases: ['boiler_load_percentage', 'boilerLoadPercentage'], type: 'number' },
      { label: 'Deck Steam Pressure', aliases: ['deck_steam_pressure', 'deckSteamPressure'], type: 'number' },
      { label: 'AE Boiler Consumption (MT)', aliases: ['ae_boiler_consumption_mt', 'aeBoilerConsumptionMt'], type: 'number' },
      {
        label: 'Deck Heat Exchanger Bunker Consumption (MT)',
        aliases: ['deck_heat_exchanger_bunker_consumption_mt', 'deckHeatExchangerBunkerConsumptionMt'],
        type: 'number',
      },
      { label: 'Deck Heater Capacity (kW)', aliases: ['deck_heater_capacity_kw', 'deckHeaterCapacityKw'], type: 'number' },
    ],
  },
  {
    id: 'composite-steam',
    title: 'Composite Boiler & Steam Output',
    fields: [
      {
        label: 'Composite Boiler Consumption Per Day',
        aliases: ['composite_boiler_consumption_per_day', 'compositeBoilerConsumptionPerDay'],
        type: 'number',
      },
      {
        label: 'Composite Boiler Fuel Consumption (kg/hr)',
        aliases: ['composite_boiler_fuel_consumption_kg_hr', 'compositeBoilerFuelConsumptionKgHr'],
        type: 'number',
      },
      {
        label: 'Steam Output – Oil Fired Section (kg/hr)',
        aliases: ['steam_output_oil_fired_section_kg_hr', 'steamOutputOilFiredSectionKgHr'],
        type: 'number',
      },
      {
        label: 'Steam Output – Exhaust ME Motor (kg/hr)',
        aliases: ['steam_output_exhaust_section_me_motor_kg_hr', 'steamOutputExhaustSectionMeMotorKgHr'],
        type: 'number',
      },
      {
        label: 'Steam Output – Exhaust AE Motor (kg/hr)',
        aliases: ['steam_output_exhaust_section_ae_motor_kg_hr', 'steamOutputExhaustSectionAeMotorKgHr'],
        type: 'number',
      },
    ],
  },
  {
    id: 'egb-economizer',
    title: 'EGB / Economizer',
    fields: [
      { label: 'EGB Capacity (kg/hr)', aliases: ['egb_capacity_kg_hr', 'egbCapacityKgHr'], type: 'number' },
      {
        label: 'Exhaust Gas Quantity (kg/hr)',
        aliases: ['egb_exhaust_gas_quantity_kg_hr', 'egbExhaustGasQuantityKgHr'],
        type: 'number',
      },
      { label: 'Gas Temperature Inlet (°C)', aliases: ['egb_gas_temp_inlet_deg_c', 'egbGasTempInletDegC'], type: 'number' },
      { label: 'Gas Temperature Outlet (°C)', aliases: ['egb_gas_temp_outlet_deg_c', 'egbGasTempOutletDegC'], type: 'number' },
    ],
  },
  {
    id: 'pipeline-summary',
    title: 'Pipeline Summary',
    spanFull: true,
    fields: [
      { label: 'Total Pipeline Length (m)', aliases: ['pipeline_length_m', 'pipelineLengthM'], type: 'number' },
      { label: 'Pipeline Thickness (mm)', aliases: ['pipeline_thickness_mm', 'pipelineThicknessMm'], type: 'number' },
      { label: 'Main Line Length (m)', aliases: ['pipeline_main_line_length_m', 'pipelineMainLineLengthM'], type: 'number' },
      {
        label: 'Main Line Thickness (mm)',
        aliases: ['pipeline_main_line_thickness_mm', 'pipelineMainLineThicknessMm'],
        type: 'number',
      },
      { label: 'Branch Line Length (m)', aliases: ['pipeline_branch_line_length_m', 'pipelineBranchLineLengthM'], type: 'number' },
      {
        label: 'Branch Line Thickness (mm)',
        aliases: ['pipeline_branch_line_thickness_mm', 'pipelineBranchLineThicknessMm'],
        type: 'number',
      },
      {
        label: 'Engine Room Length (m)',
        aliases: ['pipeline_in_engine_room_length_m', 'pipelineInEngineRoomLengthM'],
        type: 'number',
      },
      {
        label: 'Engine Room Thickness (mm)',
        aliases: ['pipeline_in_engine_room_thickness_mm', 'pipelineInEngineRoomThicknessMm'],
        type: 'number',
      },
      { label: 'Main Line Steam Insulation', aliases: ['steam_insulation_main_line', 'steamInsulationMainLine'] },
      { label: 'Branch Line Steam Insulation', aliases: ['steam_insulation_branch_line', 'steamInsulationBranchLine'] },
      {
        label: 'Engine Room Steam Insulation',
        aliases: ['steam_insulation_in_engine_room', 'steamInsulationInEngineRoom'],
      },
    ],
  },
  {
    id: 'onboarding-metadata',
    title: 'Onboarding Metadata',
    fields: [
      { label: 'Boiler Graph Applied', aliases: ['boiler_graph_applied', 'boilerGraphApplied'] },
      { label: 'Steam Table Applied', aliases: ['steam_table_applied', 'steamTableApplied'] },
      { label: 'Submitted At', aliases: ['submitted_at', 'submittedAt'], readOnly: true },
      { label: 'Updated At', aliases: ['updated_at', 'updatedAt'], readOnly: true },
      { label: 'Created At', aliases: ['created_at', 'createdAt'], readOnly: true },
    ],
  },
];

const VESSEL_FIELDS: FieldConfig[] = [
  { label: 'Vessel Name', aliases: ['vessel_name', 'vesselName', 'ship'], readOnly: true },
  { label: 'IMO Number', aliases: ['imo', 'imo_number', 'vessel_imo'], readOnly: true },
  { label: 'Client ID', aliases: ['client_id', 'clientId'], readOnly: true },
  { label: 'Segment', aliases: ['segment', 'vessel_segment'] },
  { label: 'Vessel Type', aliases: ['vessel_type', 'vesselType', 'type_of_vessel'] },
  { label: 'Tanker Type', aliases: ['tanker_type', 'tankerType', 'type_of_ship', 'typeOfShip'] },
];

export function normalizeFieldKey(key: string): string {
  return String(key ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s\-./()]+/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase();
}

export function formatFieldLabel(key: string): string {
  const normalized = normalizeFieldKey(key);
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function isMeaningfulDisplayValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  return true;
}

export function getDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'Not Available';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'Not Available';
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? 'Not Available' : value.trim();
  }
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => getDisplayValue(item)).join(', ') : 'Not Available';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function flattenResponse(
  input: unknown,
  prefix = '',
  depth = 0,
  output: Record<string, unknown> = {},
): Record<string, unknown> {
  if (!input || typeof input !== 'object' || depth > 4) {
    return output;
  }
  if (Array.isArray(input)) {
    return output;
  }

  Object.entries(input as Record<string, unknown>).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      if (!ARRAY_KEYS.has(key)) {
        output[path] = value;
      }
      return;
    }
    if (typeof value === 'object') {
      const nestedKeys = ['onboarding_data', 'onboardingData', 'data', 'json', 'config', 'cargo_heating', 'output'];
      if (nestedKeys.includes(key) || depth < 2) {
        flattenResponse(value, path, depth + 1, output);
      } else {
        output[path] = value;
      }
      return;
    }
    output[path] = value;
    output[key] = value;
  });

  return output;
}

function resolveFieldValue(
  flat: Record<string, unknown>,
  aliases: string[],
): { value: unknown; consumedKeys: string[] } {
  const normalizedIndex = new Map<string, string>();
  Object.keys(flat).forEach((key) => {
    normalizedIndex.set(normalizeFieldKey(key), key);
  });

  for (const alias of aliases) {
    if (isMeaningfulDisplayValue(flat[alias])) {
      return { value: flat[alias], consumedKeys: [alias] };
    }
    const matchedKey = normalizedIndex.get(normalizeFieldKey(alias));
    if (matchedKey && isMeaningfulDisplayValue(flat[matchedKey])) {
      return { value: flat[matchedKey], consumedKeys: [matchedKey] };
    }
  }
  return { value: undefined, consumedKeys: [] };
}

function buildRows(
  flat: Record<string, unknown>,
  fields: FieldConfig[],
  consumed: Set<string>,
): OnboardingDetailRow[] {
  return fields.map((field) => {
    const resolved = resolveFieldValue(flat, field.aliases);
    resolved.consumedKeys.forEach((key) => consumed.add(normalizeFieldKey(key)));
    field.aliases.forEach((alias) => consumed.add(normalizeFieldKey(alias)));
    return {
      label: field.label,
      value: getDisplayValue(resolved.value),
      keys: field.aliases,
      readOnly: field.readOnly,
      type: field.type,
    };
  });
}

function buildDynamicTable(
  items: unknown,
  preferredColumns: Array<{ header: string; key: string; keys: string[] }>,
): OnboardingDetailTable | null {
  if (!Array.isArray(items) || !items.length) {
    return null;
  }
  const objectRows = items.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
  if (!objectRows.length) {
    return null;
  }

  const discoveredKeys = new Set<string>();
  objectRows.forEach((item) => {
    Object.keys(item as Record<string, unknown>).forEach((key) => discoveredKeys.add(key));
  });

  const columns: Array<{ header: string; key: string; keys: string[] }> = [];
  const usedKeys = new Set<string>();
  preferredColumns.forEach((column) => {
    const match = column.keys.find((key) => discoveredKeys.has(key));
    if (match) {
      columns.push(column);
      column.keys.forEach((key) => usedKeys.add(key));
    }
  });
  [...discoveredKeys]
    .filter((key) => !usedKeys.has(key))
    .sort()
    .forEach((key) => {
      columns.push({ header: formatFieldLabel(key), key, keys: [key] });
    });

  const rows = objectRows.map((item) => {
    const record = item as Record<string, unknown>;
    return columns.map((column) => {
      for (const key of column.keys) {
        if (isMeaningfulDisplayValue(record[key])) {
          return getDisplayValue(record[key]);
        }
      }
      return 'Not Available';
    });
  });

  return {
    headers: columns.map((column) => column.header),
    columns: columns.map((column) => ({ header: column.header, key: column.key })),
    rows,
    editable: true,
  };
}

export function buildDynamicCargoHeatingDetailSections(
  raw: Record<string, unknown>,
): OnboardingDetailSection[] {
  const flat = flattenResponse(raw);
  const consumed = new Set<string>();

  const sections: OnboardingDetailSection[] = [
    {
      id: 'vessel-configuration',
      title: 'Vessel Information',
      rows: buildRows(flat, VESSEL_FIELDS, consumed),
    },
  ];

  KNOWN_SECTIONS.forEach((config) => {
    const section: OnboardingDetailSection = {
      id: config.id,
      title: config.title,
      spanFull: config.spanFull,
      rows: buildRows(flat, config.fields, consumed),
    };

    if (config.id === 'pipeline-summary') {
      section.table = buildDynamicTable(raw['pipeline_sections'] ?? raw['pipelineSections'], [
        { header: 'Segment Name', key: 'segment_name', keys: ['segment_name', 'segmentName'] },
        { header: 'Length Values (m)', key: 'length_values_m', keys: ['length_values_m', 'length_m'] },
        {
          header: 'Thickness Values (mm)',
          key: 'pipeline_thickness_values_mm',
          keys: ['pipeline_thickness_values_mm', 'pipeline_thickness_mm'],
        },
        { header: 'Steam Insulation', key: 'steam_insulation', keys: ['steam_insulation', 'steamInsulation'] },
      ]);
      ['pipeline_sections', 'pipelineSections'].forEach((key) => consumed.add(normalizeFieldKey(key)));
    }

    sections.push(section);
  });

  const tankTable = buildDynamicTable(raw['tanks'] ?? raw['heating_tanks'], [
    { header: 'Tank Name', key: 'tank_name', keys: ['tank_name', 'name', 'tankName'] },
    { header: 'Capacity (m³)', key: 'capacity_m3', keys: ['capacity_m3', 'capacity', 'capacityM3'] },
    { header: 'Heating Surface (m²)', key: 'heating_surface_m2', keys: ['heating_surface_m2', 'heatingSurfaceM2'] },
    {
      header: 'Steam Consumption (kg/h)',
      key: 'steam_consumption_kgh',
      keys: ['steam_consumption_kgh', 'steam_consumption_kg_hr', 'max_steam', 'maxSteam'],
    },
  ]);
  ['tanks', 'heating_tanks'].forEach((key) => consumed.add(normalizeFieldKey(key)));

  sections.push({
    id: 'tank-heating-grid',
    title: 'Tank / Heating Grid Information',
    spanFull: true,
    rows: [],
    table: tankTable ? { ...tankTable, dataKey: 'tanks' } : null,
    emptyMessage: tankTable ? undefined : 'No tank / heating grid data available.',
  });

  const liquidCargo = resolveLiquidCargoRows(raw);
  const liquidBuilt = buildLiquidCargoDynamicTable(liquidCargo);
  const liquidApplied =
    raw['liquid_cargo_applied'] === true || raw['liquidCargoApplied'] === true;
  const liquidTable: OnboardingDetailTable | null = liquidBuilt
    ? {
        headers: liquidBuilt.headers,
        rows: liquidBuilt.rows,
        columns: liquidBuilt.columns,
        columnKeys: liquidBuilt.columnKeys,
        rawRows: liquidBuilt.rawRows,
        dynamic: true,
        dataKey: 'liquid_cargo',
        editable: true,
      }
    : null;

  [
    'liquid_cargo',
    'liquidCargo',
    'liquid_cargo_applied',
    'liquidCargoApplied',
    'liquid_cargo_fill_note',
    'liquidCargoFillNote',
  ].forEach((key) => consumed.add(normalizeFieldKey(key)));

  sections.push({
    id: 'liquid-cargo-table',
    title: 'Liquid Cargo Table',
    spanFull: true,
    rows: buildRows(
      flat,
      [
        { label: 'Liquid Cargo Applied', aliases: ['liquid_cargo_applied', 'liquidCargoApplied'] },
        { label: 'Liquid Cargo Fill Note', aliases: ['liquid_cargo_fill_note', 'liquidCargoFillNote'] },
      ],
      consumed,
    ),
    table: liquidTable,
    emptyMessage: liquidTable
      ? undefined
      : liquidApplied
        ? LIQUID_CARGO_EMPTY_MESSAGE
        : 'Liquid cargo is not applicable for this vessel.',
  });

  sections.push({
    id: 'uploaded-docs',
    title: 'Uploaded Documents',
    rows: [],
    files: extractUploadedFilesFromRow(raw),
    readOnly: true,
    editable: false,
  });
  ['uploaded_files', 'uploadedFiles'].forEach((key) => consumed.add(normalizeFieldKey(key)));

  const unmappedRows: OnboardingDetailRow[] = [];
  Object.entries(flat).forEach(([key, value]) => {
    const baseKey = key.includes('.') ? key.split('.').pop() ?? key : key;
    if (EXCLUDED_KEYS.has(baseKey) || ARRAY_KEYS.has(baseKey)) {
      return;
    }
    if (consumed.has(normalizeFieldKey(key)) || consumed.has(normalizeFieldKey(baseKey))) {
      return;
    }
    if (!isMeaningfulDisplayValue(value) || typeof value === 'object') {
      return;
    }
    if (!key.includes('.')) {
      const isDuplicateLeaf = Object.keys(flat).some((k) => k !== key && k.endsWith(`.${key}`));
      if (isDuplicateLeaf) {
        return;
      }
    }
    unmappedRows.push({
      label: formatFieldLabel(baseKey),
      value: getDisplayValue(value),
      keys: [key],
      readOnly: true,
    });
  });

  if (unmappedRows.length) {
    sections.push({
      id: 'additional-configuration',
      title: 'Additional Configuration',
      spanFull: true,
      rows: unmappedRows,
      readOnly: true,
      editable: false,
    });
  }

  return sections;
}
